<template>
  <!--
    Shared, presentational order-detail sidebar (the left "preview" column) for
    the list views OrdersList.vue and OnlineOrders.vue. Step 1 extraction of the
    ~300-line `.orders-sidebar` block that was duplicated (with small, real,
    runtime-only-verifiable differences) between those two views.

    Like OrderListCard, this component is intentionally "dumb": it performs NO
    pricing/discount/refund/balance math. Each parent view computes its own
    display values and passes them in props-down (scalars) or as function-props
    (per-item / per-order formatters), and supplies the structurally-divergent
    inner blocks (modifier rows, the item discount row) via scoped slots. Card
    actions are reported events-up (refund / cancel / toggle-details /
    print-check) so each view keeps its own handlers.

    Divergence handled, per the original two templates:
      - order-no text                  -> `orderNoText` prop
      - refund button visibility       -> `showRefundButton` + `refundDisabled`
      - item key / name / line price   -> `itemKey` / `itemName` / `itemPriceText`
      - item button extra classes      -> `itemButtonClass` (OrdersList only)
      - modifier rows                  -> `item-modifiers` scoped slot
      - item discount row              -> `item-discount` scoped slot
      - collapse visibility            -> `collapseVisible` (OrdersList toggles)
      - collapse price / discount      -> `collapsePriceText` / `collapseDiscountText`
      - totals (discount/surcharge/...) -> function-props + `surchargeLabel`
      - balance                        -> `balanceText`
      - desktop print disabled         -> `printDisabled`

    The OrdersList-only mobile print panel and each view's TableOrders-style
    swiper are intentionally NOT part of this component (they live outside the
    `.orders-sidebar` element); see the per-view templates / QA checklist.
  -->
  <div class="orders-sidebar">
    <div class="orders-head">
      <div class="orders-top">
        <div class="d-flex align-items-center">
          <div class="orders-top-num">
            <span>
              {{ $t("amountandpaymentmethod.order-no") }}
            </span>
            <strong>
              # {{ orderNoText }}{{ order.dining_option ? " - " + order.dining_option : "" }}
            </strong>
          </div>
          <div class="orders-top-btn ml-auto d-flex align-items-center">
            <b-button
              v-if="showRefundButton"
              variant="secondary"
              :disabled="refundDisabled"
              @click="$emit('refund')"
            >
              {{ order.isFullyRefund ? "Fully Refunded" : $t("orderscreenv1.refund") }}
            </b-button>
            <b-button
              v-if="showEdit"
              :to="editTo"
              variant="secondary"
              :disabled="actionsDisabled"
            >
              {{ $t("amountandpaymentmethod.edit-order") }}
            </b-button>
            <b-button
              v-if="showEditCancel"
              variant="secondary"
              :disabled="actionsDisabled"
              @click="$emit('cancel')"
            >
              {{ $t("orderscreen.cancel-order") }}
            </b-button>
          </div>
        </div>
      </div>
      <div class="orders-add">
        <span v-if="!order.customer">{{ $t("orderscreen.no-customer-selected") }}</span>
        <span v-else>
          {{ order.customer.first_name }} {{ order.customer.last_name }}
        </span>
      </div>
    </div>

    <div class="orders-sidebar-body">
      <div
        class="orders-list"
        role="tablist"
      >
        <b-card
          v-for="(item, index) in items"
          :key="itemKey(item, index)"
          no-body
          class="list-complete-item"
        >
          <b-card-header
            header-tag="header"
            class="orders-item-head p-0"
            role="tab"
          >
            <b-button
              v-b-toggle="'accordion-'+index"
              block
              href="#"
              :class="itemButtonClass"
            >
              <div class="d-flex">
                <div class="btn-num">
                  {{ item.quantity }}
                </div>

                <div class="btn-body">
                  <div class="d-flex align-items-center justify-content-between top text-nowrap">
                    <div class="text-left d-flex">
                      {{ itemName(item) }}
                      <svg
                        v-if="item.notes&&item.notes.length"
                        class="icon icon-icon-note"
                      >
                        <use xlink:href="#icon-icon-note" />
                      </svg>
                      <div
                        v-if="item.isPartialRefund"
                        class="icon-dot ml-2 my-auto"
                      />
                    </div>
                    <div class="text-right">
                      <span
                        v-if="item.hasOwnProperty('tax_rates') && item.tax_rates.length > 0"
                        class="short-desc"
                      >{{ $t("listvieworderdelegate.taxed") }}</span>
                      {{ currency }} {{ itemPriceText(item) }}
                    </div>
                  </div>
                  <slot
                    name="item-modifiers"
                    :item="item"
                  />
                  <slot
                    name="item-discount"
                    :item="item"
                  />
                </div>
              </div>
            </b-button>
          </b-card-header>

          <b-collapse
            :id="'accordion-'+index"
            :visible="collapseVisible(index)"
            accordion="my-accordion"
            role="tabpanel"
          >
            <b-card-body>
              <table class="table">
                <tr>
                  <td>
                    <span>
                      {{ $t("listvieworderdelegate.qty") }}
                    </span>
                    <strong class="text-primary">
                      {{ item.quantity }}
                    </strong>
                  </td>
                  <td>
                    <span>
                      {{ $t("listvieworderdelegate.price") }}
                    </span>
                    <strong class="text-primary">
                      {{ currency }}{{ collapsePriceText(item) }}
                    </strong>
                  </td>
                  <td>
                    <span>
                      {{ $t("listvieworderdelegate.discount") }}
                    </span>
                    <strong>
                      {{ currency }} {{ collapseDiscountText(item) }}
                    </strong>
                  </td>
                </tr>
              </table>
              <div
                v-if="item.notes&&item.notes.length"
                class="card-notes"
              >
                {{ item.notes }}
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </div>

    <div class="orders-bottom">
      <div v-if="!displayPaymentDetails">
        <div class="orders-bottom-item">
          <span class="text-left">
            {{ $t("ordertotal.total") }}
          </span>
          <span class="text-right">
            {{ currency }} {{ totalText(order) }}
            <b-button
              variant="link"
              class="w-100"
              @click="$emit('toggle-details')"
            >
              {{ $t("ordertotal.view-details") }}
            </b-button>
          </span>
        </div>
      </div>

      <div v-else>
        <div class="orders-bottom-item">
          <b-button
            variant="link"
            class="w-100 text-center"
            @click="$emit('toggle-details')"
          >
            {{ $t("ordertotal.close-details") }}
          </b-button>
        </div>
        <div class="orders-bottom-item">
          <span class="text-left">
            {{ $t("ordertotal.discount") }}
          </span>
          <span class="text-right">
            {{ currency }} {{ discountTotalText(order) }}
          </span>
        </div>
        <div class="orders-bottom-item">
          <span class="text-left">
            {{ $t("ordertotal.sub-total") }}
          </span>
          <span class="text-right">
            {{ currency }} {{ order.subtotal.toFixed(2) }}
          </span>
        </div>

        <div class="orders-bottom-item">
          <span class="text-left">
            {{ $t("ordertotal.tax") }}
          </span>
          <span class="text-right">
            {{ currency }} {{ order ? order.tax_total.toFixed(2) : "0.00" }}
          </span>
        </div>

        <div
          class="orders-bottom-item"
          v-if="order.tips_total"
        >
          <span class="text-left">
            {{ $t("ordertotal.tip") }}
          </span>
          <span class="text-right">
            {{ currency }} {{ order.tips_total.toFixed(2) }}
          </span>
        </div>

        <div
          v-if="order.surcharge_value > 0"
          class="orders-bottom-item"
        >
          <span class="text-left">
            {{ surchargeLabel }}
          </span>
          <span class="text-right">
            {{ currency }} {{ surchargeValueText(order) }}
          </span>
        </div>

        <div class="orders-bottom-item">
          <span class="text-left">
            {{ $t("ordertotal.total") }}
          </span>
          <span class="text-right">
            {{ currency }} {{ totalText(order) }}
          </span>
        </div>
      </div>

      <div
        v-if="!(order.status === ORDER_STATUS.HOLD || order.status === ORDER_STATUS.CANCELLED)"
      >
        <div
          v-for="(payment, index) in order.payments"
          :key="index"
          class="orders-bottom-item"
        >
          <span class="text-left">
            {{ payment.amount > 0 ? $t("ordertotal.paid") : "Refunded" }} - {{ payment.payment_type }}
          </span>
          <span class="text-right">
            {{ currency }} {{ paymentChargedAmount(payment).toFixed(2) }}
            <span :style="{color: 'red'}">
              {{ `(${payment.change_amount.toFixed(2)})` }}
            </span>
          </span>
        </div>
      </div>
      <div class="orders-bottom-options">
        <div class="order-sum">
          <div class="d-flex align-items-center justify-content-between">
            <span class="text-left">
              {{ $t("ordertotal.balance") }}
            </span>
            <span class="text-right">
              {{ currency }} {{ balanceText(order) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Print panel For desktop -->
      <div class="print d-none d-md-block">
        <div class="d-flex h-100 no-gutters">
          <div class="h-100 w-100">
            <b-button
              :disabled="printDisabled(order)"
              variant="link"
              class="w-100"
              @click="$emit('print-check')"
            >
              <svg class="icon icon-icon-print-1">
                <use xlink:href="#icon-icon-print-1" />
              </svg>

              <span>
                {{ $t("amountandpaymentmethod.print-check") }}
              </span>
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ORDER_STATUS } from "@/helpers/order/status";
import { TYPE_KITCHENHUB_ORDER } from "@/helpers/order/kitchenHubOrderMapper";

export default {
  name: "OrderDetailSidebar",
  props: {
    // The previewed order. Read-only here; never mutated by this component.
    order: {
      type: Object,
      required: true
    },
    // Items to render (already void-filtered by the parent, e.g.
    // itemsAtCurrentOrder from orderMixin).
    items: {
      type: Array,
      default: () => []
    },
    currency: {
      type: String,
      default: ""
    },
    // Pre-formatted order-no (e.g. "Table 1 / 42" or "42").
    orderNoText: {
      type: [String, Number],
      default: ""
    },
    showRefundButton: {
      type: Boolean,
      default: false
    },
    refundDisabled: {
      type: Boolean,
      default: false
    },
    // Disables the edit/cancel buttons (OrdersList passes isLoading; the value
    // false renders no `disabled` attribute, matching OnlineOrders' markup).
    actionsDisabled: {
      type: Boolean,
      default: false
    },
    // Whether the expanded "view details" totals block is shown. Owned by the
    // parent and toggled via the `toggle-details` event.
    displayPaymentDetails: {
      type: Boolean,
      default: false
    },
    // "Surcharge" (OrdersList) vs "Non-Cash Fee" (OnlineOrders).
    surchargeLabel: {
      type: String,
      default: "Surcharge"
    },
    // Extra static class(es) for the item accordion button (OrdersList passes
    // "btn-secondary btn-block" to preserve its existing duplicate tokens).
    itemButtonClass: {
      type: String,
      default: ""
    },
    // ---- function-props (per-item / per-order formatters) ----
    itemKey: {
      type: Function,
      default: (item, index) => index
    },
    itemName: {
      type: Function,
      default: (item) => (item ? item.name : "")
    },
    itemPriceText: {
      type: Function,
      default: () => "0.00"
    },
    collapseVisible: {
      type: Function,
      default: () => true
    },
    collapsePriceText: {
      type: Function,
      default: () => "0.00"
    },
    collapseDiscountText: {
      type: Function,
      default: () => "0"
    },
    totalText: {
      type: Function,
      default: () => "0.00"
    },
    discountTotalText: {
      type: Function,
      default: () => "0"
    },
    surchargeValueText: {
      type: Function,
      default: () => "0.00"
    },
    balanceText: {
      type: Function,
      default: () => "0.00"
    },
    printDisabled: {
      type: Function,
      default: () => false
    }
  },
  data () {
    return {
      ORDER_STATUS
    };
  },
  computed: {
    // Both views show edit/cancel for OPEN or HOLD orders, with the same target
    // route, so this stays internal to keep the call sites tiny.
    showEditCancel () {
      return this.order.status === ORDER_STATUS.OPEN || this.order.status === ORDER_STATUS.HOLD;
    },
    // KitchenHub orders are priced/settled on the delivery platform's side —
    // adding/removing items or changing quantities here would desync from
    // what was actually charged. Cancel stays available (it's a lifecycle
    // action that pushes a status update back to KitchenHub, not an edit).
    showEdit () {
      return this.showEditCancel && this.order.type !== TYPE_KITCHENHUB_ORDER;
    },
    editTo () {
      return { name: "Order", params: { id: this.order.id || this.order.local_id } };
    }
  },
  methods: {
    // payment.amount mirrors order.grand_total only (tip/surcharge live on
    // their own Payment fields, see api/orderlive/views.py), so it understates
    // what the customer was actually charged on this tender. Add them back in
    // for display only — the stored fields stay separate.
    paymentChargedAmount (payment) {
      return (payment.amount || 0) + (payment.tips || 0) + (payment.surcharge || 0);
    }
  }
};
</script>
